<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2020 (original work) Open Assessment Technologies SA ;
    import { createEventDispatcher } from 'svelte';
    import { dragScrollable, isRTLElement } from '@oat-sa-private/ui-core';
    import { Icon, IconBarButton, Badge } from '@oat-sa-private/ui-elements';
    import { throttle } from 'lodash';

    const dispatch = createEventDispatcher();

    let scrollboxElement;
    let scrollboxElementWidth;
    let needsLeftScroll;
    let needsRightScroll;
    let scrollTarget; // target value of smooth scroll

    /**
     * @example of tabs array format:
     * [{
     *    key: 'docs',
     *    label: 'My Documents',
     *    disabled: false
     * }]
     */
    export let tabs = [];
    export let activeTab = tabs.length && tabs[0].key;
    export let fullwidth = false;
    export let disabled = false;

    /**
     * Evalute needs scroll variables
     */
    const calculateNeedsScroll = throttle(() => {
        if (!scrollboxElement) {
            return;
        }
        const { scrollWidth } = scrollboxElement;
        const scrollLeft = Math.round(scrollboxElement.scrollLeft);
        if (isRTLElement(scrollboxElement)) {
            needsRightScroll = needsScroll && scrollLeft !== 0;
            needsLeftScroll = needsScroll && -scrollLeft < scrollWidth - scrollboxElementWidth;
        } else {
            needsLeftScroll = needsScroll && scrollLeft !== 0;
            needsRightScroll = needsScroll && scrollLeft < scrollWidth - scrollboxElementWidth;
        }
    }, 200);

    $: needsScroll = scrollboxElement && scrollboxElementWidth < scrollboxElement.scrollWidth;
    $: scrollboxElement && scrollboxElementWidth && calculateNeedsScroll();

    /**
     * Active tab handler
     * @param {String} tab - the key property of tab to activate
     */
    function activateTab(tab) {
        activeTab = tab;
        dispatch('change', { key: activeTab });
    }

    /**
     * Callback when smooth scroll reached target value
     */
    function scrollReachedCallback() {
        if (scrollboxElement.scrollLeft.toFixed() === scrollTarget) {
            calculateNeedsScroll();
        }
    }

    /**
     * Scroll scrollbox horizontally
     * @param {number} dx - scroll amount
     * @param {boolean} smooth - should the scroll be smooth
     */
    function scrollScrollBox(dx, smooth = false) {
        scrollboxElement.removeEventListener('scroll', scrollReachedCallback);
        if (smooth) {
            const { scrollLeft, scrollWidth } = scrollboxElement;
            scrollTarget = scrollLeft + dx;

            // put between limits
            if (isRTLElement(scrollboxElement)) {
                scrollTarget = Math.max(Math.min(scrollTarget, 0), -(scrollWidth - scrollboxElementWidth));
            } else {
                scrollTarget = Math.min(Math.max(scrollTarget, 0), scrollWidth - scrollboxElementWidth);
            }

            scrollTarget = scrollTarget.toFixed(); // to round the number

            scrollboxElement.addEventListener('scroll', scrollReachedCallback);
            scrollboxElement.scrollTo({
                left: scrollTarget,
                behavior: 'smooth'
            });
        } else {
            scrollboxElement.scrollLeft = scrollboxElement.scrollLeft - dx;
        }
    }

    /**
     * Handler for dragscroll event
     * @param {CustomEvent} event
     */
    function handleDragscroll(event) {
        scrollScrollBox(event.detail.dx);
        calculateNeedsScroll();
    }

    /**
     * Handle tab focus and scroll to make it visible
     * @param {Event} e
     */
    function handleTabFocus(e) {
        const tab = e.currentTarget;
        const { offsetLeft, clientWidth } = tab;
        const { scrollLeft } = scrollboxElement;
        const scrollButtonWidth = 44;
        let dx = 0;

        if (offsetLeft < scrollLeft + scrollButtonWidth) {
            dx = scrollLeft - offsetLeft + scrollButtonWidth;
        } else if (offsetLeft + clientWidth > scrollLeft + scrollboxElementWidth - scrollButtonWidth) {
            dx = scrollLeft + scrollboxElementWidth - scrollButtonWidth - offsetLeft - clientWidth;
        }

        if (dx) {
            scrollScrollBox(dx);
            calculateNeedsScroll();
        }
    }
</script>

<style>
    .tabgroup {
        position: relative;
    }
    .scrollbox {
        position: relative;
        border-bottom: var(--border-thin) solid var(--color-border-default);
        margin-bottom: var(--space-2x);

        /* These horizontal overflow settings should apply on small devices */
        overflow-x: auto;
        -ms-overflow-style: none; /* Internet Explorer 10+ */
        scrollbar-width: none; /* Firefox */
        &::-webkit-scrollbar {
            display: none; /* Safari and Chrome */
        }
    }
    .scroll-button {
        position: absolute;
        z-index: 2; /* to be above active underline */
        background-color: var(--color-bg-default);
        top: 0;

        &:after {
            content: '';
            position: absolute;
            top: 0;
            width: 1.5rem;
            height: 100%;
        }

        &.left {
            left: 0;
            &:after {
                right: -12px;
                background: linear-gradient(
                    90deg,
                    rgba(18, 18, 18, 0.25) 0%,
                    rgba(18, 18, 18, 0.1) 51.56%,
                    rgba(217, 217, 217, 0) 100%
                );
            }
        }

        &.right {
            right: 0;
            &:after {
                left: -12px;
                background: linear-gradient(
                    270deg,
                    rgba(18, 18, 18, 0.25) 0%,
                    rgba(18, 18, 18, 0.1) 51.56%,
                    rgba(217, 217, 217, 0) 100%
                );
            }
        }
    }
    ul {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-wrap: nowrap;
    }
    button {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;

        /* mini reset */
        border: none;
        border-radius: 0;
        background: none;

        color: var(--color-text-default);
        font-size: var(--fontsize-body-s);
        text-transform: capitalize;
        @add-mixin bold;
        white-space: nowrap;

        min-width: 5.5rem; /* because of hitbox */
        margin: 0;
        padding: 1.75rem 1.5rem 1.25rem;
        line-height: 2.5rem;
        letter-spacing: inherit;
        word-spacing: inherit;

        cursor: pointer;

        /* thick bottom border effect */
        &[aria-selected='true'] {
            &::before {
                content: '';
                position: absolute;
                left: 0;
                bottom: 0;
                width: 100%;
                height: 0.5rem;
                background: var(--color-border-default);
                z-index: 1;
            }
        }

        &:disabled {
            color: var(--color-text-disabled);
            cursor: not-allowed;
            font-weight: 400;
        }

        & .success {
            color: var(--color-text-success);
        }
        & .warning {
            color: var(--color-text-warning);
        }
    }
    .fullwidth {
        flex-grow: 1;
    }
    .fullwidth > button {
        width: 100%;
    }
    li :global-nested(button) {
        &:hover,
        &:focus,
        &:active {
            background: var(--color-bg-actionable-secondary-hover);
        }
        @add-mixin outline-focus 0.5rem;
    }
    .icon-container {
        margin-inline-end: var(--space-half);

        &.warning {
            color: var(--color-bg-warning);
        }
    }
</style>

<div class="tabgroup">
    {#if needsLeftScroll}
        <div class="scroll-button left">
            <IconBarButton
                icon="chevron-left-12"
                shape="circular"
                on:click={() => scrollScrollBox((-scrollboxElementWidth * 2) / 3, true)} />
        </div>
    {/if}
    <div
        class="scrollbox"
        use:dragScrollable
        on:dragscroll={handleDragscroll}
        bind:this={scrollboxElement}
        bind:clientWidth={scrollboxElementWidth}>
        <ul role="tablist">
            {#each tabs as tab}
                <li role="presentation" class:fullwidth>
                    <button
                        role="tab"
                        disabled={disabled || tab.disabled}
                        aria-selected={tab.key === activeTab}
                        aria-current={tab.key === activeTab}
                        on:click={() => activateTab(tab.key)}
                        on:focus={handleTabFocus}>
                        {#if tab.icon}
                            <span class:warning={tab.icon.warning} class="icon-container">
                                <Icon name={tab.icon.name} ariaHidden />
                            </span>
                        {/if}
                        {#if Array.isArray(tab.label)}
                            {#each tab.label as label, index}
                                {#if index !== 0}&nbsp;{/if}<span class={(label && label.class) || void 0}
                                    >{label.text || label}</span>
                            {/each}
                        {:else}
                            {tab.label}
                        {/if}
                        {#if tab.badge}
                            <Badge {...tab.badge} />
                        {/if}
                    </button>
                </li>
            {/each}
        </ul>
    </div>
    {#if needsRightScroll}
        <div class="scroll-button right">
            <IconBarButton
                icon="chevron-right-12"
                shape="circular"
                on:click={() => scrollScrollBox((scrollboxElementWidth * 2) / 3, true)} />
        </div>
    {/if}
</div>
